import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Icon from "../../components/AppIcon";
import { InlineLoader } from "../../components/ui/Loader";
import { adminGet } from "../../api/adminClient";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [productTypeStats, setProductTypeStats] = useState(null);
  const [adminUserStats, setAdminUserStats] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const [prods, types, users] = await Promise.all([
          adminGet("/admin/products?page=1&per_page=1"),
          adminGet("/admin/products/product-types?page=1&per_page=1"),
          adminGet("/admin/settings/users?include_inactive=true"),
        ]);

        const prodList = Array.isArray(prods) ? prods : [];
        const prodTotal = prods?.total ?? prods?.stats?.total ?? prodList.length;
        setProductStats({
          total: prodTotal,
          in_stock: prods?.stats?.in_stock ?? prodList.filter((p) => (p.availability || "In Stock") === "In Stock").length,
          pre_order: prods?.stats?.pre_order ?? prodList.filter((p) => p.availability === "Pre-Order").length,
          out_of_stock: prods?.stats?.out_of_stock ?? prodList.filter((p) => p.availability === "Out of Stock").length,
        });

        const typeList = Array.isArray(types) ? types : types?.items ?? [];
        const typeActive = typeList.filter((t) => t.is_active).length;
        setProductTypeStats({
          total_types: types?.stats?.total_types ?? typeList.length,
          active_types: types?.stats?.active_types ?? typeActive,
          inactive_or_deleted_types: types?.stats?.inactive_or_deleted_types ?? typeList.length - typeActive,
        });

        const arr = Array.isArray(users) ? users : [];
        const totalAdmins = arr.length;
        const activeAdmins = arr.filter((u) => u.is_active).length;
        setAdminUserStats({
          total: totalAdmins,
          active: activeAdmins,
          inactive: totalAdmins - activeAdmins,
        });
      } catch (e) {
        if (e?.status !== 401 && !e?.isSessionExpired) {
          setError(e?.message || "Failed to load dashboard stats");
        }
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const StatCard = ({ title, value, description, details, icon, colorClass, onClick }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`bg-card rounded-xl shadow-lg border border-border p-4 sm:p-6 ${onClick ? "cursor-pointer hover:shadow-xl transition-shadow" : ""}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-text-secondary text-xs sm:text-sm font-medium uppercase tracking-wide">
            {title}
          </p>
          <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mt-1 sm:mt-2 break-words overflow-hidden text-ellipsis">
            {value}
          </p>
          {description && (
            <p className="text-text-secondary text-xs sm:text-sm mt-1">{description}</p>
          )}
          {details && details.length > 0 && (
            <div className="mt-3 space-y-1 text-xs text-text-secondary">
              {details.map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          )}
        </div>
        <div
          className={`w-11 h-11 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}
        >
          <Icon name={icon} size={24} className="text-white sm:w-7 sm:h-7" />
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <InlineLoader text="Loading dashboard stats…" size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome banner - premium gradient, primary stays theme */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-r from-primary to-accent rounded-xl p-6 text-primary-foreground"
      >
        <h1 className="text-2xl font-bold mb-2">Welcome back, Admin!</h1>
        <p className="text-white/90">Here&apos;s what&apos;s happening with your platform today.</p>
      </motion.div>

      {error && (
        <div className="p-3 rounded-lg bg-error/10 border border-error/20 text-error text-sm">
          {error}
        </div>
      )}

      {/* Single row: stats + description + detail lines (no duplicate row) */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Products"
          value={productStats?.total ?? 0}
          description="Total products in catalog"
          details={[
            <>In stock: <span className="font-semibold text-success">{productStats?.in_stock ?? 0}</span></>,
            <>Pre‑order: <span className="font-semibold text-accent">{productStats?.pre_order ?? 0}</span></>,
            <>Out of stock: <span className="font-semibold text-error">{productStats?.out_of_stock ?? 0}</span></>,
          ]}
          icon="Package"
          colorClass="bg-blue-500"
          onClick={() => navigate("/admin/products")}
        />
        <StatCard
          title="Product Types"
          value={productTypeStats?.total_types ?? 0}
          description="Categories in the catalog"
          details={[
            <>Active: <span className="font-semibold text-success">{productTypeStats?.active_types ?? 0}</span></>,
            <>Inactive / deleted: <span className="font-semibold text-warning">{productTypeStats?.inactive_or_deleted_types ?? 0}</span></>,
          ]}
          icon="FolderTree"
          colorClass="bg-green-500"
          onClick={() => navigate("/admin/product-types")}
        />
        <StatCard
          title="Admin Users"
          value={adminUserStats?.total ?? 0}
          description="Admins with panel access"
          details={[
            <>Active: <span className="font-semibold text-success">{adminUserStats?.active ?? 0}</span></>,
            <>Inactive: <span className="font-semibold text-text-secondary">{adminUserStats?.inactive ?? 0}</span></>,
          ]}
          icon="Users"
          colorClass="bg-primary"
        />
      </div>

      {/* Quick Actions - multiple colors, premium style */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="bg-card rounded-xl shadow-lg p-6 border border-border"
      >
        <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/admin/product-types" className="no-underline">
            <div className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-blue-500 flex items-center justify-center mr-3 shrink-0">
                <Icon name="FolderTree" size={24} className="text-white" />
              </div>
              <span className="text-blue-900 font-medium">Product Types</span>
            </div>
          </Link>
          <Link to="/admin/products" className="no-underline">
            <div className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors border border-green-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-green-500 flex items-center justify-center mr-3 shrink-0">
                <Icon name="Package" size={24} className="text-white" />
              </div>
              <span className="text-green-900 font-medium">Products</span>
            </div>
          </Link>
          <Link to="/admin/settings" className="no-underline">
            <div className="flex items-center p-4 bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors border border-primary/20">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-primary flex items-center justify-center mr-3 shrink-0">
                <Icon name="Settings" size={24} className="text-primary-foreground" />
              </div>
              <span className="text-primary font-medium">Settings</span>
            </div>
          </Link>
          <Link to="/admin/settings/roles" className="no-underline">
            <div className="flex items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors border border-purple-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-purple-500 flex items-center justify-center mr-3 shrink-0">
                <Icon name="Shield" size={24} className="text-white" />
              </div>
              <span className="text-purple-900 font-medium">Roles</span>
            </div>
          </Link>
          <Link to="/admin/settings/permissions" className="no-underline">
            <div className="flex items-center p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors border border-amber-100">
              <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-amber-500 flex items-center justify-center mr-3 shrink-0">
                <Icon name="Key" size={24} className="text-white" />
              </div>
              <span className="text-amber-900 font-medium">Permissions</span>
            </div>
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
